/**
 * Settings — Import Newsletters.
 *
 * Choose the printed newsletters. The page reads each one's page count, draws its front
 * cover, works out the date from the filename, and shelves them.
 *
 * THE TICK-BOX IS THE WHOLE CONTROL, and it is worth understanding before moving it. An
 * issue is only readable by supporters if somebody states that the faces in it are
 * already covered; that statement is the single thing standing between a photograph of a
 * child and every donor with a password. Nothing here can check it — this page can render
 * a cover, and neither it nor the Worker can look at a photograph and tell you whether the
 * people in it are recognisable. So it is deliberately an action a person takes and not a
 * default: unticked shelves the issues for staff only, which is safe and useless, and that
 * is the right way round for a box somebody might tick without reading. Imports & Users → Import
 * Import Annual Reports asks the same question the same way, for the same reason.
 *
 * WHERE THIS DIFFERS FROM THE REPORTS PAGE is the size of a batch. There is one report a
 * year and there are three or four newsletters, so the whole back catalogue is dozens of
 * files chosen at once rather than one. That is why the prepared issues are a grid of
 * thumbnails instead of a card each, why reading them counts itself out loud, and why a
 * file the page will not take is reported by name and skipped rather than stopping the
 * run — one bad filename in forty should not mean starting again.
 */
import { useState } from 'react'
import { PageHeader } from '../App'
import { useApi, api } from '../lib/api'
import { readShelfPdf, dateOf } from '../lib/reportPdf'
import {
  Card, Badge, Notice, Empty, Loading, ErrorState, ConfirmModal,
  useToast, fmtBytes, fmtDate,
} from '../components/ui'
import { Dropzone } from '../components/Dropzone'
import { FacesWarning, WhoCanRead } from '../components/ShelfImport'

const phaseLabel = (p) => (p?.phase === 'cover' ? 'Drawing the cover…' : 'Reading the file…')

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * What an issue is called on the shelf: "March 2014 Newsletter".
 *
 * Sliced out of the stored text rather than read through a Date, for the same reason the
 * shelf does it — 'YYYY-MM-DD' is midnight UTC to a Date, which west of Greenwich is the
 * day before, and an issue should not be titled differently depending on who imported it.
 */
const titleFor = (iso) =>
  `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)} Newsletter`

export default function SettingsNewsletterIssues({ user }) {
  const shelf = useApi('newsletter-issues', 'newsletter-issues')
  const toast = useToast()
  const [prepared, setPrepared] = useState([])
  const [working, setWorking] = useState(null)
  const [progress, setProgress] = useState(null)
  const [shelving, setShelving] = useState(null)
  const [removing, setRemoving] = useState(null)
  // Never remembered between batches. Whether the faces are covered is a fact about the
  // files in front of you, and carrying a tick over from the last batch shelved would be
  // the application answering the one question it exists to ask.
  const [covered, setCovered] = useState(false)

  const mayImport = user?.caps?.manageSettings && user?.caps?.uploadMedia
  if (!mayImport) {
    return (
      <PageHeader title="Import Newsletters">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Shelving a newsletter writes a file to the archive, so it is kept to owners and
          administrators. You are signed in as <strong>{user?.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const choose = async (fileList) => {
    const pdfs = Array.from(fileList ?? []).filter((f) => /\.pdf$/i.test(f.name))
    if (!pdfs.length) {
      toast.error('Choose one or more newsletter PDFs.')
      return
    }
    const out = []
    let n = 0
    for (const file of pdfs) {
      n++
      const date = dateOf(file.name)
      if (!date) {
        // Named rather than counted. The usual cause is a day that is not on the
        // calendar — one of these files was called "2022 11 31" — and the person reading
        // this toast is the one who can rename it.
        toast.error(
          `${file.name}: no real date in the filename, so it was skipped. ` +
            'A filename needs a year, a month and a day that exists.'
        )
        continue
      }
      setWorking({ name: file.name, date, n, of: pdfs.length })
      setProgress(null)
      try {
        // The date is attached here rather than inside the reader, and `rightHalf` is
        // false: these are ordinary portrait pages and page one is the cover, where the
        // annual reports are imposed two-up for print. See readShelfPdf.
        out.push({
          date,
          ...(await readShelfPdf(file, {
            title: titleFor(date),
            rightHalf: false,
            onProgress: setProgress,
          })),
        })
      } catch (err) {
        toast.error(`${file.name}: ${err.message || 'could not be read'}`)
      }
    }
    setWorking(null)
    setProgress(null)
    // Newest first, so the batch on the screen is in the order the shelf will show it in.
    out.sort((a, b) => b.date.localeCompare(a.date))
    setPrepared(out)
  }

  const shelveAll = async () => {
    let done = 0
    for (const [i, r] of prepared.entries()) {
      setShelving({ n: i + 1, of: prepared.length, date: r.date })
      const fd = new FormData()
      fd.set('issue_date', r.date)
      fd.set('title', r.title)
      fd.set('page_count', String(r.page_count))
      fd.set('file', r.files.file, `${r.date} Newsletter.pdf`)
      fd.set('cover', r.files.cover, `${r.date}.jpg`)
      // What makes the Worker store the file under both editions. Sent only when it is
      // true, so an unticked box is an absent claim rather than a false one.
      if (covered) fd.set('faces_covered', '1')
      try {
        await api('newsletter-issues', { method: 'POST', formData: fd })
        done++
      } catch (err) {
        toast.error(`${r.date}: ${err.message || 'could not be shelved'}`)
      }
    }
    setShelving(null)
    for (const r of prepared) URL.revokeObjectURL(r.coverUrl)
    setPrepared([])
    setCovered(false)
    await shelf.refetch()
    if (done) {
      toast.ok(
        `Shelved ${done} newsletter${done === 1 ? '' : 's'}` +
          (covered ? ', readable by supporters.' : ' for staff only.')
      )
    }
  }

  const remove = async (row) => {
    try {
      await api(`newsletter-issues/${row.id}`, { method: 'DELETE' })
      toast.ok(`Removed the ${fmtDate(row.issue_date)} newsletter.`)
      await shelf.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setRemoving(null)
    }
  }

  const startAgain = () => {
    for (const r of prepared) URL.revokeObjectURL(r.coverUrl)
    setPrepared([])
    setCovered(false)
  }

  const busy = !!shelving

  return (
    <PageHeader
      title="Import Newsletters"
      sub="The issues on the shelf, and how to add more"
      actions={
        prepared.length ? (
          <button className="btn" onClick={startAgain} disabled={busy}>
            Start again
          </button>
        ) : null
      }
    >
      <div className="col">
        {!prepared.length && !working && (
          <Card
            title="Add Issues"
            sub="Cover the faces in the PDFs, choose them here, and they are shelved as they stand."
          >
            <Notice tone="info" icon="newspaper" title="How an issue gets onto the shelf">
              <ol className="small" style={{ margin: 0, paddingLeft: '1.2em' }}>
                <li>
                  Open the printed newsletter in whatever you use to edit a PDF and blur or
                  black out every face in it. Nothing in this application does this for
                  you, and nothing here can check that it was done.
                </li>
                <li>
                  Save it with the date in the filename — <span className="mono">2014 03
                  28 S4CA-Newsletter.pdf</span> is the shape these come in, and dashes,
                  dots and underscores work as well as spaces. The date is where the issue
                  goes on the shelf, so a day that is not on the calendar is refused rather
                  than guessed at.
                </li>
                <li>
                  Choose the whole folder at once if you like — this page reads them one
                  after another and tells you about any it could not take.
                </li>
                <li>
                  Tick to say the faces are covered. That tick is what lets a view-only
                  account and a donor read the issues; without it they are shelved for
                  staff only and supporters are told they are unavailable.
                </li>
              </ol>
            </Notice>

            <Dropzone
              onFiles={choose}
              accept="application/pdf"
              multiple
              icon="newspaper"
              title="Choose the newsletter PDFs"
              warn={<FacesWarning />}
            >
              Drop them here, or click to pick — select them all. The date is read from each
              filename, and the front cover is drawn from the first page.
            </Dropzone>
            <p className="small subtle" style={{ margin: 0 }}>
              Nothing leaves this browser until you press Shelve.
            </p>
          </Card>
        )}

        {working && (
          <Card
            title={`Reading ${working.n} of ${working.of}`}
            sub={`${working.name} — ${fmtDate(working.date)}`}
          >
            <div className="row">
              <Loading label={phaseLabel(progress)} rows={1} />
            </div>
          </Card>
        )}

        {prepared.length > 0 && (
          <>
            <Card
              title={`Ready to Shelve`}
              sub={`${prepared.length} issue${prepared.length === 1 ? '' : 's'}, newest first`}
              actions={<Badge tone="ok">Read</Badge>}
            >
              <p className="small subtle" style={{ marginTop: 0 }}>
                Each file will be shelved exactly as it stands, under the date in its
                filename. These are the printed covers — if the faces on any of them are
                not covered, that file is not ready.
              </p>
              <div className="report-shelf prepared-shelf">
                {prepared.map((r) => (
                  <div className="report-card" key={r.date}>
                    <div className="report-cover">
                      <img src={r.coverUrl} alt={`Front cover of the ${r.date} newsletter`} />
                    </div>
                    <div className="report-meta">
                      <div className="report-year">{fmtDate(r.date)}</div>
                      <div className="report-title truncate">{r.title}</div>
                      <div className="report-note">
                        {r.page_count} pages · {fmtBytes(r.bytes)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Shelve">
              <WhoCanRead
                covered={covered}
                onCovered={setCovered}
                count={prepared.length}
                item="issue"
                kind="newsletter"
              />
              <div className="row">
                <button className="btn btn-primary" onClick={shelveAll} disabled={busy}>
                  {shelving
                    ? `Shelving ${shelving.n} of ${shelving.of}…`
                    : `Shelve ${prepared.length} newsletter${prepared.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </Card>
          </>
        )}

        <Card title="On the Shelf" sub="Newest first">
          {shelf.isLoading ? (
            <Loading rows={3} />
          ) : shelf.isError ? (
            <ErrorState error={shelf.error} retry={shelf.refetch} />
          ) : !shelf.data?.rows?.length ? (
            <Empty icon="newspaper" title="No newsletters yet" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Title</th>
                    <th>Pages</th>
                    <th>Size</th>
                    <th>Supporters can open it</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shelf.data.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="mono nowrap">{r.issue_date}</td>
                      <td>{r.title}</td>
                      <td className="nowrap">{r.page_count ? `${r.page_count} pp` : '—'}</td>
                      <td className="nowrap">{r.bytes ? fmtBytes(r.bytes) : '—'}</td>
                      <td>
                        {r.has_anon_edition ? (
                          <Badge tone="ok">Yes</Badge>
                        ) : (
                          <Badge tone="warn">No — not shown to them</Badge>
                        )}
                      </td>
                      <td className="nowrap">
                        <button className="btn btn-sm" onClick={() => setRemoving(r)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <ConfirmModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        title={`Remove the ${removing ? fmtDate(removing.issue_date) : ''} newsletter?`}
        body="The file and its cover are deleted from storage. The issue can be shelved again at any time."
        confirmLabel="Remove"
      />
    </PageHeader>
  )
}
